"use client";

import React from "react";
import { Dropdown as AntDropdown, ConfigProvider } from "antd";
import type { DropdownProps as AntDropdownProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { CheckOutlined } from "@ant-design/icons";
import "../styles/dropdown.css";

export interface DropdownProps extends AntDropdownProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Dropdown component - Ant Design Dropdown with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Dropdown props while
 * automatically applying the OpenStore Orion theme.
 *
 * Features:
 * - Orion design specifications (colors, typography, spacing)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Dropdown features and props
 *
 * @example
 * ```tsx
 * // Basic dropdown with Orion theming
 * <Dropdown
 *   menu={{
 *     items: [
 *       { key: '1', label: 'Option 1' },
 *       { key: '2', label: 'Option 2' },
 *       { key: '3', label: 'Option 3' },
 *     ]
 *   }}
 * >
 *   <Button>Click me</Button>
 * </Dropdown>
 *
 * // All Ant Design Dropdown props work
 * <Dropdown
 *   menu={{
 *     items: menuItems,
 *     onClick: handleMenuClick,        // ✅ Menu click handler
 *     selectable: true,                // ✅ Selectable items
 *     multiple: true,                  // ✅ Multiple selection
 *     selectedKeys: selectedKeys,      // ✅ Selected state
 *     defaultSelectedKeys: ['1'],      // ✅ Default selection
 *   }}
 *   trigger={['click', 'hover']}       // ✅ Trigger events
 *   placement="bottomLeft"             // ✅ Placement
 *   arrow                              // ✅ Arrow indicator
 *   disabled={isDisabled}              // ✅ Disabled state
 *   autoFocus                          // ✅ Auto focus
 *   autoAdjustOverflow                 // ✅ Auto adjust
 *   getPopupContainer={(node) => node.parentElement} // ✅ Custom container
 *   overlayClassName="custom-dropdown" // ✅ Overlay styling
 *   overlayStyle={{ minWidth: 200 }}  // ✅ Overlay style
 *   onOpenChange={handleOpenChange}    // ✅ Open/close handler
 *   open={isOpen}                      // ✅ Controlled open state
 *   destroyPopupOnHide                 // ✅ Destroy on hide
 * >
 *   <Button>Advanced Dropdown</Button>
 * </Dropdown>
 *
 * // Dropdown with icons and dividers
 * <Dropdown
 *   menu={{
 *     items: [
 *       {
 *         key: 'edit',
 *         label: 'Edit',
 *         icon: <EditOutlined />,
 *       },
 *       {
 *         key: 'copy',
 *         label: 'Copy',
 *         icon: <CopyOutlined />,
 *       },
 *       {
 *         type: 'divider',
 *       },
 *       {
 *         key: 'delete',
 *         label: 'Delete',
 *         icon: <DeleteOutlined />,
 *         danger: true,
 *       },
 *     ],
 *     onClick: ({ key }) => handleAction(key),
 *   }}
 *   trigger={['contextMenu']}
 * >
 *   <div>Right click me</div>
 * </Dropdown>
 *
 * // Dropdown with sub-menus
 * <Dropdown
 *   menu={{
 *     items: [
 *       {
 *         key: 'export',
 *         label: 'Export',
 *         children: [
 *           { key: 'pdf', label: 'Export as PDF' },
 *           { key: 'excel', label: 'Export as Excel' },
 *           { key: 'csv', label: 'Export as CSV' },
 *         ],
 *       },
 *       {
 *         key: 'import',
 *         label: 'Import',
 *         children: [
 *           { key: 'upload', label: 'Upload File' },
 *           { key: 'url', label: 'From URL' },
 *         ],
 *       },
 *     ],
 *   }}
 * >
 *   <Button>File Operations</Button>
 * </Dropdown>
 *
 * // With Tailwind overrides
 * <Dropdown
 *   menu={{ items: menuItems }}
 *   overlayClassName="shadow-2xl border-orion-200 rounded-xl"
 *   className="custom-dropdown-trigger"
 * >
 *   <Button className="shadow-lg">Custom Styled</Button>
 * </Dropdown>
 *
 * // Searchable dropdown (using AutoComplete pattern)
 * <Dropdown
 *   menu={{
 *     items: filteredItems,
 *     onClick: handleSelect,
 *   }}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * >
 *   <Input.Search
 *     placeholder="Search and select..."
 *     onSearch={handleSearch}
 *     onChange={(e) => handleInputChange(e.target.value)}
 *   />
 * </Dropdown>
 * ```
 */
export const Dropdown: React.FC<DropdownProps> = ({
  className,
  children,
  menu,
  ...props // All other Ant Design props pass through
}) => {
  // Enhanced menu with Figma design specifications
  const enhancedMenu = menu
    ? {
        ...menu,
        style: {
          borderRadius: 6,
          padding: 4,
          boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.09)",
          border: "1px solid #F1F5F9",
          ...menu.style,
        },
        items: menu.items?.map((item: any) => ({
          ...item,
          style: {
            borderRadius: 6,
            padding: "2px 8px",
            margin: "1px 0",
            ...item.style,
          },
          className: clsx(
            "orion-dropdown-menu-item",
            {
              "orion-dropdown-menu-item-hover": true,
              "orion-dropdown-menu-item-selected": item.selected,
            },
            item.className
          ),
        })),
      }
    : undefined;

  return (
    <ConfigProvider theme={orionTheme}>
      <AntDropdown
        className={clsx(
          "orion-dropdown",
          // Add custom classes for Tailwind overrides
          className
        )}
        menu={enhancedMenu}
        {...props} // Pass through ALL Ant Design props
      >
        {children}
      </AntDropdown>
    </ConfigProvider>
  );
};

Dropdown.displayName = "Dropdown";

// Export convenience components for common patterns
export const ContextMenuDropdown: React.FC<Omit<DropdownProps, "trigger">> = (
  props
) => <Dropdown trigger={["contextMenu"]} {...props} />;

export const HoverDropdown: React.FC<Omit<DropdownProps, "trigger">> = (
  props
) => <Dropdown trigger={["hover"]} {...props} />;

export const ClickDropdown: React.FC<Omit<DropdownProps, "trigger">> = (
  props
) => <Dropdown trigger={["click"]} {...props} />;

export const ActionDropdown: React.FC<
  DropdownProps & {
    actions: Array<{
      key: string;
      label: string;
      icon?: React.ReactNode;
      danger?: boolean;
      disabled?: boolean;
    }>;
    onAction?: (key: string) => void;
  }
> = ({ actions, onAction, ...props }) => (
  <Dropdown
    menu={{
      items: actions.map((action) => ({
        key: action.key,
        label: action.label,
        icon: action.icon,
        danger: action.danger,
        disabled: action.disabled,
      })),
      onClick: ({ key }) => onAction?.(key),
    }}
    {...props}
  />
);

// SelectDropdown - Matches Figma design specifications
export interface SelectDropdownProps extends Omit<DropdownProps, "menu"> {
  options: Array<{
    key: string;
    label: string;
    value: string;
  }>;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  showCheckIcon?: boolean;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  placeholder = "text",
  onChange,
  showCheckIcon = true,
  className,
  children,
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find((option) => option.value === value);

  const menuItems = options.map((option) => ({
    key: option.key,
    label: (
      <div className="flex items-center justify-between w-full">
        <span className="text-xs font-normal" style={{ color: "#333333" }}>
          {option.label}
        </span>
        {showCheckIcon && value === option.value && (
          <CheckOutlined
            className="orion-dropdown-check-icon"
            style={{
              fontSize: "12px",
              color: "#1D4A88",
            }}
          />
        )}
      </div>
    ),
    selected: value === option.value,
    onClick: () => {
      onChange?.(option.value);
      setIsOpen(false);
    },
  }));

  return (
    <Dropdown
      menu={{
        items: menuItems,
        style: {
          borderRadius: 6,
          padding: 4,
          boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.09)",
          border: "1px solid #F1F5F9",
          minWidth: 200,
        },
      }}
      trigger={["click"]}
      open={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      {...props}
    >
      {children || (
        <div
          className={clsx("orion-select-dropdown-trigger", { open: isOpen })}
        >
          <span className="text-xs font-normal" style={{ color: "#333333" }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className="orion-select-dropdown-chevron"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ width: "16px", height: "16px" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      )}
    </Dropdown>
  );
};
